import React, { useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../services/firebase";
import { useSeller } from "../contexts/SellerContext";
import { getMetalRates } from "../services/metalRates";

const DashboardDebug = () => {
  const { seller } = useSeller();
  const [orders, setOrders] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [rtgsRate, setRtgsRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parseNum = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getTimeValue = (value) => {
    if (!value) return 0;
    if (typeof value === "number") {
      if (value < 10000000000) return value * 1000;
      return value;
    }
    if (value?.seconds) return value.seconds * 1000;
    if (value?._seconds) return value._seconds * 1000;
    if (value?.toDate && typeof value.toDate === "function") return value.toDate().getTime();
    if (typeof value === "string") {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  const getOrderTimestamp = (order) => {
    return (
      getTimeValue(order.createdAt) ||
      getTimeValue(order.orderCreatedAt) ||
      getTimeValue(order.timestamp) ||
      getTimeValue(order.orderDate) ||
      getTimeValue(order.date) ||
      getTimeValue(order.createdDate) ||
      getTimeValue(order.updatedAt) ||
      getTimeValue(order.settlementTime) ||
      0
    );
  };

  const getOrderFineWeight = (order) => {
    const directFine = parseNum(order.fineWeight || order.totalFine || order.fineWt || order.totalFineWeight);
    if (directFine > 0) return directFine;

    const netWt = parseNum(order.netWt || order.displayNetWt || order.totalNetWt);
    const wastage = parseNum(order.wastage);
    const purity = parseNum(order.purity);
    const specificationMC = parseNum(order.specificationMC);
    
    if (netWt === 0) return 0;
    
    const addOnFactor = (purity + wastage + specificationMC) / 100;
    return netWt * addOnFactor;
  };

  const getOrderMc = (order) => {
    const directMc = parseNum(order.totalMc || order.totalMC || order.makingCharges || order.makingCharge || order.mc);
    if (directMc > 0) return directMc;

    const netWt = parseNum(order.netWt || order.displayNetWt || order.totalNetWt);
    const qty = parseNum(order.orderPiece || order.quantity || order.orderQuantity || order.totalPieces) || 1;
    const specWt = parseNum(order.specificationWt || order.specWt) || netWt;
    const setMc = parseNum(order.setMc || order.setMakingCharge);
    const gramMc = parseNum(order.netGramMc || order.gramMc || order.makingChargePerGram);
    const specMc = parseNum(order.specificationGramRate || order.specMcPerGram || order.specGramRate);

    return setMc * qty + gramMc * netWt + specMc * specWt;
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching orders for seller:", seller.sellerId);
      
      // Fetch RTGS rate
      const rates = await getMetalRates({ 
        useLiveApi: true, 
        state: seller?.state 
      });
      
      const metalType = seller?.segment?.toLowerCase() || "gold";
      const rtgsSaleRate = rates?.[metalType]?.RTGS_SALE_RATE || 0;
      setRtgsRate(rtgsSaleRate);
      console.log("RTGS Sale Rate:", rtgsSaleRate);

      // Fetch orders
      const q = query(
        collection(db, "orderList"),
        where("sellerId", "==", seller.sellerId)
      );
      
      const snapshot = await getDocs(q);
      console.log("Documents found:", snapshot.size);
      
      const ordersData = [];
      snapshot.forEach((doc) => {
        ordersData.push({ id: doc.id, ...doc.data() });
      });
      
      setOrders(ordersData);
      console.log("Orders:", ordersData);

      // Calculate metrics
      const now = Date.now();
      const oneDayAgo = now - 24 * 60 * 60 * 1000;
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
      const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

      const calculatePeriodMetrics = (startTime) => {
        const filteredOrders = ordersData.filter((order) => {
          if (!startTime) return true; // All Time
          const orderTime = getOrderTimestamp(order);
          if (orderTime === 0) return startTime <= thirtyDaysAgo;
          return orderTime >= startTime;
        });

        let totalOrders = 0;
        let salesFine = 0;
        let salesMc = 0;
        let receivedFine = 0;
        let receivedMc = 0;

        filteredOrders.forEach((order) => {
          const status = (order.orderStatus || order.status || "").toLowerCase();
          const fineWt = getOrderFineWeight(order);
          const mc = getOrderMc(order);

          // Sales group: any order excluding status "requested"
          const isSalesOrOrder = !status.includes("requested");
          if (isSalesOrOrder) {
            totalOrders++;
            salesFine += fineWt;
            salesMc += mc;
          }

          // Received group: settled orders (sellerPaymentVerified === true or equivalent)
          const isSettled =
            order.sellerPaymentVerified === true ||
            order.settlement?.sellerPaymentVerified === true ||
            order.paymentVerified === true ||
            status === "settled" ||
            status.includes("settled");

          if (isSettled) {
            receivedFine += fineWt;
            receivedMc += mc;
          }
        });

        return { 
          totalOrders, 
          salesFine, 
          salesMc, 
          receivedFine, 
          receivedMc 
        };
      };

      const todayMetrics = calculatePeriodMetrics(oneDayAgo);
      const weekMetrics = calculatePeriodMetrics(sevenDaysAgo);
      const monthMetrics = calculatePeriodMetrics(thirtyDaysAgo);
      const allTimeMetrics = calculatePeriodMetrics(null);

      // Format metrics for display
      const formatWeight = (val) => `${parseFloat(val || 0).toFixed(3)}g`;
      const formatRupee = (amount) =>
        `₹${parseFloat(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;

      setMetrics({
        today: {
          salesFine: formatWeight(todayMetrics.salesFine),
          salesMc: formatRupee(todayMetrics.salesMc),
          orders: Math.round(todayMetrics.totalOrders).toString(),
          receivedFine: formatWeight(todayMetrics.receivedFine),
          receivedMc: formatRupee(todayMetrics.receivedMc)
        },
        week: {
          salesFine: formatWeight(weekMetrics.salesFine),
          salesMc: formatRupee(weekMetrics.salesMc),
          orders: Math.round(weekMetrics.totalOrders).toString(),
          receivedFine: formatWeight(weekMetrics.receivedFine),
          receivedMc: formatRupee(weekMetrics.receivedMc)
        },
        month: {
          salesFine: formatWeight(monthMetrics.salesFine),
          salesMc: formatRupee(monthMetrics.salesMc),
          orders: Math.round(monthMetrics.totalOrders).toString(),
          receivedFine: formatWeight(monthMetrics.receivedFine),
          receivedMc: formatRupee(monthMetrics.receivedMc)
        },
        allTime: {
          salesFine: formatWeight(allTimeMetrics.salesFine),
          salesMc: formatRupee(allTimeMetrics.salesMc),
          orders: Math.round(allTimeMetrics.totalOrders).toString(),
          receivedFine: formatWeight(allTimeMetrics.receivedFine),
          receivedMc: formatRupee(allTimeMetrics.receivedMc)
        }
      });

    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatRupee = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;

  const formatOrderDate = (order) => {
    const ts = getOrderTimestamp(order);
    if (!ts) return "No Date";
    return new Date(ts).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div style={{ padding: "20px", fontFamily: "sans-serif", maxWidth: "1200px", margin: "0 auto" }}>
      <h1>Dashboard Debug & Business Overview</h1>
      
      <div style={{ background: "#f0f0f0", padding: "15px", marginBottom: "20px", borderRadius: "8px" }}>
        <h3>Seller Info:</h3>
        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold", padding: "5px" }}>Seller ID:</td>
              <td style={{ padding: "5px" }}>{seller?.sellerId || "N/A"}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold", padding: "5px" }}>State:</td>
              <td style={{ padding: "5px" }}>{seller?.state || "N/A"}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold", padding: "5px" }}>Segment:</td>
              <td style={{ padding: "5px" }}>{seller?.segment || "N/A"}</td>
            </tr>
            {rtgsRate > 0 && (
              <tr>
                <td style={{ fontWeight: "bold", padding: "5px" }}>RTGS Sale Rate:</td>
                <td style={{ padding: "5px" }}>₹{rtgsRate.toLocaleString()}/gram</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <button 
        onClick={fetchOrders}
        style={{ 
          padding: "12px 24px", 
          fontSize: "16px", 
          cursor: "pointer",
          background: "#d99a00",
          color: "white",
          border: "none",
          borderRadius: "8px",
          fontWeight: "600"
        }}
      >
        {loading ? "Loading..." : "Fetch Orders & Calculate Metrics"}
      </button>

      {error && (
        <div style={{ color: "red", marginTop: "20px", padding: "15px", background: "#ffe6e6", borderRadius: "8px" }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {metrics && (
        <div style={{ marginTop: "30px" }}>
          <h2>Business Overview Cards</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "15px", marginBottom: "30px" }}>
            {/* Today */}
            <div style={{ background: "#fff", border: "2px solid #d99a00", borderRadius: "12px", padding: "15px" }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#d99a00" }}>Today</h3>
              <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                <div><strong>Sales Fine:</strong> {metrics.today.salesFine}</div>
                <div><strong>Sales MC:</strong> {metrics.today.salesMc}</div>
                <div><strong>Orders:</strong> {metrics.today.orders}</div>
                <div><strong>Received Fine:</strong> {metrics.today.receivedFine}</div>
                <div><strong>Received MC:</strong> {metrics.today.receivedMc}</div>
              </div>
            </div>

            {/* 7 Days */}
            <div style={{ background: "#fff", border: "2px solid #059669", borderRadius: "12px", padding: "15px" }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#059669" }}>Last 7 Days</h3>
              <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                <div><strong>Sales Fine:</strong> {metrics.week.salesFine}</div>
                <div><strong>Sales MC:</strong> {metrics.week.salesMc}</div>
                <div><strong>Orders:</strong> {metrics.week.orders}</div>
                <div><strong>Received Fine:</strong> {metrics.week.receivedFine}</div>
                <div><strong>Received MC:</strong> {metrics.week.receivedMc}</div>
              </div>
            </div>

            {/* 30 Days */}
            <div style={{ background: "#fff", border: "2px solid #0369a1", borderRadius: "12px", padding: "15px" }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#0369a1" }}>Last 30 Days</h3>
              <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                <div><strong>Sales Fine:</strong> {metrics.month.salesFine}</div>
                <div><strong>Sales MC:</strong> {metrics.month.salesMc}</div>
                <div><strong>Orders:</strong> {metrics.month.orders}</div>
                <div><strong>Received Fine:</strong> {metrics.month.receivedFine}</div>
                <div><strong>Received MC:</strong> {metrics.month.receivedMc}</div>
              </div>
            </div>

            {/* All Time */}
            <div style={{ background: "#fff", border: "2px solid #7c3aed", borderRadius: "12px", padding: "15px" }}>
              <h3 style={{ margin: "0 0 10px 0", color: "#7c3aed" }}>All Time (Total)</h3>
              <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                <div><strong>Sales Fine:</strong> {metrics.allTime.salesFine}</div>
                <div><strong>Sales MC:</strong> {metrics.allTime.salesMc}</div>
                <div><strong>Orders:</strong> {metrics.allTime.orders}</div>
                <div><strong>Received Fine:</strong> {metrics.allTime.receivedFine}</div>
                <div><strong>Received MC:</strong> {metrics.allTime.receivedMc}</div>
              </div>
            </div>
          </div>

          <div style={{ background: "#e7f3ff", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
            <h4 style={{ margin: "0 0 10px 0" }}>📊 Metrics Logic:</h4>
            <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "13px" }}>
              <li><strong>Orders:</strong> Count of all orders (status NOT "Requested")</li>
              <li><strong>Sales Fine:</strong> Sum of fine weight (g) of all orders (status NOT "Requested")</li>
              <li><strong>Sales MC:</strong> Sum of making charges (₹) of all orders (status NOT "Requested")</li>
              <li><strong>Received Fine:</strong> Sum of fine weight (g) of settled orders (sellerPaymentVerified === true / settled)</li>
              <li><strong>Received MC:</strong> Sum of making charges (₹) of settled orders (sellerPaymentVerified === true / settled)</li>
            </ul>
          </div>
        </div>
      )}

      {orders.length > 0 && (
        <div style={{ marginTop: "30px" }}>
          <h2>All Orders ({orders.length})</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
                  <th style={{ padding: "10px", textAlign: "left" }}>#</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Order ID</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Date</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Status</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Net Wt (g)</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Purity</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Wastage</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Fine Wt (g)</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>MC (₹)</th>
                  <th style={{ padding: "10px", textAlign: "center" }}>In Sales?</th>
                  <th style={{ padding: "10px", textAlign: "center" }}>In Received?</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => {
                  const status = (order.orderStatus || order.status || "").toLowerCase();
                  const netWt = parseNum(order.netWt || order.displayNetWt || order.totalNetWt);
                  const fineWt = getOrderFineWeight(order);
                  const mc = getOrderMc(order);
                  
                  const isSalesOrOrder = !status.includes("requested");
                  
                  const isSettled =
                    order.sellerPaymentVerified === true ||
                    order.settlement?.sellerPaymentVerified === true ||
                    order.paymentVerified === true ||
                    status === "settled" ||
                    status.includes("settled");

                  return (
                    <tr key={order.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "10px" }}>{idx + 1}</td>
                      <td style={{ padding: "10px", fontFamily: "monospace", fontSize: "11px" }}>{order.id}</td>
                      <td style={{ padding: "10px", fontSize: "11px", color: "#666" }}>{formatOrderDate(order)}</td>
                      <td style={{ padding: "10px" }}>
                        <span style={{ 
                          padding: "3px 8px", 
                          borderRadius: "4px", 
                          background: isSettled ? "#d1fae5" : isSalesOrOrder ? "#dbeafe" : "#f3f4f6",
                          fontSize: "11px",
                          fontWeight: "600"
                        }}>
                          {order.orderStatus || order.status || "N/A"}
                        </span>
                      </td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{netWt.toFixed(3)}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{order.purity || 0}%</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{order.wastage || 0}%</td>
                      <td style={{ padding: "10px", textAlign: "right", fontWeight: "600", color: "#059669" }}>{fineWt.toFixed(3)}g</td>
                      <td style={{ padding: "10px", textAlign: "right", fontWeight: "600", color: "#0369a1" }}>{formatRupee(mc)}</td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {isSalesOrOrder ? "✅" : "❌"}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        {isSettled ? "✅" : "❌"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {orders.length === 0 && !loading && !error && (
        <div style={{ marginTop: "20px", padding: "20px", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px" }}>
          <strong>No orders found!</strong>
          <p>Check:</p>
          <ol>
            <li>Firebase Console → Firestore → orderList collection</li>
            <li>Look for documents with sellerId = {seller?.sellerId}</li>
            <li>If no orders exist, create test orders in Firestore</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default DashboardDebug;